import React, { ReactNode, useEffect, useMemo, useState } from "react";

import { Col, Container, Row } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";

import type { OperationVariables } from "@apollo/client";

import {
  FilterBy,
  ListGroupedPaymentLinesQuery,
  ListGroupedPaymentLinesQueryVariables,
  type PaymentLineDetailsWithResaleDetails,
  type RemittanceWithPaymentLines,
  type ResaleFragment,
  UserStatus,
} from "@api/graphql";
import { PendingNotification } from "@components/PendingNotification";
import { UserDisplay } from "@components/UserDisplay";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import { ProtectedComponent } from "@components/permission_control/ProtectedComponent";
import DateFilter from "@components/table/DateFilter";
import SearchInput from "@components/table/SearchInput";
import { useAuthenticationContext } from "@context/authentication";
import { DetailsDrawer } from "@pages/dashboard/DetailsDrawer";
import { type PaginatedOutputGeneric, getPageHeading } from "@util/index";
import type { QueryFunction, QueryOptions } from "@util/query";

import { HorizontalButtonPager } from "../table/Pager";
import { RoyaltiesTable } from "../table/RoyaltiesTable";
import type { TableHeader } from "../table/TableContents";

import styles from "./Royalties.module.scss";

export type RoyaltiesSourcePageType = "UpcomingRoyalties" | "PastPayments";

interface GroupedRoyaltiesProps<TData, TVariables extends OperationVariables> {
  queryFunction: QueryFunction<TData, TVariables>;
  getQueryVariables: (page: number) => TVariables;
  updateQueryVariablesWithFilter: (
    currentQueryVariables: TVariables,
    new_filter_by: FilterBy[] | undefined,
  ) => TVariables;
  getRemittancesFromQueryData: (data: TData) => RemittanceWithPaymentLines[];
  getPaginatedOutputFromQueryData: (data: TData) => PaginatedOutputGeneric<RemittanceWithPaymentLines>;
  getAccordionHeader?: (remittance: RemittanceWithPaymentLines) => ReactNode;
  customTableHeaders?: TableHeader<PaymentLineDetailsWithResaleDetails>[];
  sourcePage?: RoyaltiesSourcePageType;
  customTableStyleClassName?: string;
  displayBanner?: boolean;
}

export const GroupedRoyalties = ({
  queryFunction,
  getQueryVariables,
  updateQueryVariablesWithFilter,
  getRemittancesFromQueryData,
  getPaginatedOutputFromQueryData,
  getAccordionHeader,
  customTableHeaders,
  sourcePage,
  customTableStyleClassName,
  displayBanner = true,
}: GroupedRoyaltiesProps<ListGroupedPaymentLinesQuery, ListGroupedPaymentLinesQueryVariables>) => {
  const { auth } = useAuthenticationContext();

  const [page, setPage] = useState(1);
  const [remittance, setRemittance] = useState<RemittanceWithPaymentLines[]>([]);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState<{ startDate: string | null; endDate: string | null }>({
    startDate: null,
    endDate: null,
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentSelectedRowResaleData, setCurrentSelectedRowResaleData] = useState<ResaleFragment>();

  const filters = useMemo(() => {
    const filterArray: FilterBy[] = [];
    if (searchText) {
      filterArray.push({ field: "search_name", iLike: `%${searchText}%` });
    }
    if (dateRange.startDate) {
      filterArray.push({ field: "date_created", gte: dateRange.startDate });
    }
    if (dateRange.endDate) {
      filterArray.push({ field: "date_created", lte: dateRange.endDate });
    }
    return filterArray;
  }, [searchText, dateRange]);

  const queryVariables = useMemo(() => {
    const baseVariables = getQueryVariables(page);
    return updateQueryVariablesWithFilter(baseVariables, filters);
  }, [getQueryVariables, page, updateQueryVariablesWithFilter, filters]);

  const queryOptions: QueryOptions<ListGroupedPaymentLinesQuery, ListGroupedPaymentLinesQueryVariables> = useMemo(
    () => ({
      variables: queryVariables,
      fetchPolicy: "no-cache",
      errorPolicy: "none",
    }),
    [queryVariables],
  );

  const { data, loading, refetch } = queryFunction(queryOptions);

  const paginatedOutput = data ? getPaginatedOutputFromQueryData(data) : undefined;

  useEffect(() => {
    if (data) {
      const remittance = getRemittancesFromQueryData(data);
      setRemittance(remittance);
    }
  }, [data, getRemittancesFromQueryData]);

  useEffect(() => {
    if (!currentSelectedRowResaleData) {
      return;
    }

    const currentRowResaleId = currentSelectedRowResaleData.resale_id;

    for (const eachRemittance of remittance) {
      const currentUpdatedRow = eachRemittance.payment_lines?.find(
        (payment_line) => payment_line?.resale?.resale_id === currentRowResaleId,
      );

      if (currentUpdatedRow) {
        setCurrentSelectedRowResaleData(currentUpdatedRow.resale);
        break;
      }
    }

    // this useEffect is used for updating the row data when the API refetch,
    // so not putting other dependencies as row click handler will handle that case
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remittance]);

  useEffect(() => {
    setPage(1);
    refetch();
  }, [filters, refetch]);

  const handleOnTableRowClick = (rowData: PaymentLineDetailsWithResaleDetails) => {
    setCurrentSelectedRowResaleData(rowData.resale);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const pageHeading = getPageHeading(sourcePage, "Royalties");

  const enterPressed = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearchText(e.currentTarget.value);
    }
  };

  return (
    <>
      {currentSelectedRowResaleData && (
        <DetailsDrawer
          resale={currentSelectedRowResaleData}
          isDrawerOpen={isDrawerOpen}
          refetch={refetch}
          onHide={handleDrawerClose}
        />
      )}
      <Container className="p-4" fluid>
        {displayBanner && (
          <>
            <Row className="pb-4">
              <Col>
                <UserDisplay />
              </Col>
            </Row>
            <ProtectedComponent requiredUserStatus={UserStatus.PENDING_APPROVAL} auth={auth}>
              <Row key={"invoice"}>
                <PendingNotification title={<span>We are still reviewing your account.</span>} />
              </Row>
            </ProtectedComponent>
            <Row>
              <Col>
                <h3>{pageHeading.heading}</h3>
                <span className="b3">{pageHeading.subheading}</span>
              </Col>
            </Row>
          </>
        )}
        <Row className="mt-3">
          <Col md={3}>
            <SearchInput searchText={searchText} setSearchText={setSearchText} enterPressed={enterPressed} />
          </Col>
          <Col md={3}>
            <DateFilter setDateRange={setDateRange} />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <Row>
              <Col>
                {loading && (
                  <div className="w-100 d-flex justify-content-center">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  </div>
                )}
                {!loading &&
                  remittance.map((eachRemittance, index) => {
                    const { payment_lines, id } = eachRemittance;
                    return (
                      (payment_lines?.length || 0) > 0 && (
                        <div key={id} className={styles["row-border"]}>
                          <CustomAccordion
                            accordionKey={`${id}-${index}`}
                            header={getAccordionHeader?.(eachRemittance)}
                            customHeaderStyle="px-2"
                          >
                            <div className="p-2 pt-0 d-flex justify-content-start align-items-start align-self-stretch flex-column">
                              <RoyaltiesTable
                                onRowClick={handleOnTableRowClick}
                                paymentLines={payment_lines ?? []}
                                customTableHeaders={customTableHeaders}
                                customTableStyleClassName={customTableStyleClassName}
                              />
                            </div>
                          </CustomAccordion>
                        </div>
                      )
                    );
                  })}
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <HorizontalButtonPager
                  page={page}
                  totalPages={paginatedOutput?.total_pages || 0}
                  totalItems={paginatedOutput?.total_items || 0}
                  setPage={setPage}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
};
